import React from 'react';
import { STATIONS, Station, StationId, StationCounts, ZONE_LABELS, Zone, LINE_LABELS, LineId, stationIndex } from './lineModel';

/**
 * The legislative process as a metro map. "explain" is the walkthrough in the
 * pop-up (big stations, a train at the selected one); "live" sits on
 * /legislation/pipeline with dossier counts per station and acts as its filter.
 *
 * Two drawings of the same map: horizontal when the map is at least 880px
 * wide, vertical below (a container query in global.css picks one; the other
 * is display:none, so it is out of the tab order and the accessibility tree).
 */

type Variant = 'explain' | 'live';

interface LineMapProps {
  variant: Variant;
  selected: StationId | null;
  onSelect: (id: StationId) => void;
  counts?: StationCounts;
  /** Accessible name of the group of station buttons. */
  label: string;
  /** Draw the line key on the map itself (top left), above it when vertical. */
  legend?: boolean;
}

// Horizontal drawing: a 1040-wide viewBox stretched to the map's width (x in %,
// y in px, strokes kept at their width with non-scaling-stroke).
const H = {
  width: 1040,
  /** The live map needs room for counts under the labels. */
  height: { explain: 330, live: 350 } as Record<Variant, number>,
  zoneLabelY: { explain: 304, live: 322 } as Record<Variant, number>,
  x: { feedback: 64, tabled: 196, vote: 328, scrutiny: 528, adopted: 734, published: 858, applies: 978 } as Record<StationId, number>,
  trunkY: 194,
  branchY: 92,
  lineS: 'M 64 186 L 388 186 L 464 92 L 592 92 L 668 186 L 978 186',
  lineE: 'M 64 202 L 978 202',
  zones: [[0, 388], [388, 668], [668, 1040]] as Array<[number, number]>,
  labelWidth: 104,
};

// Vertical drawing: one row per station, the Line S loop to the left. The
// first and last rows sit fully inside the box, so their focus rings show.
const V = {
  row: 78,
  top: 40,
  trunkX: 82,
  branchX: 26,
  labelX: 132,
  lineS: 'M 74 40 L 74 212 L 26 252 L 26 296 L 74 336 L 74 508',
  lineE: 'M 90 40 L 90 508',
  height: 548,
  zones: [[0, 235], [235, 313], [313, 548]] as Array<[number, number]>,
};

const RADIUS: Record<Variant, { h: number; v: number }> = {
  explain: { h: 34, v: 24 },
  live: { h: 26, v: 22 },
};

/** The two lines, as a key above the map. `short` drops the examples. */
export function LineLegend({ short = false }: { short?: boolean }) {
  return (
    <ul className="line-legend">
      {(['S', 'E'] as LineId[]).map(line => (
        <li key={line} className="line-legend__item">
          <span className={`line-legend__swatch line-legend__swatch--${line.toLowerCase()}`} aria-hidden="true" />
          <strong>{LINE_LABELS[line].name}</strong>
          {' '}{short ? LINE_LABELS[line].long.split(':')[0] : LINE_LABELS[line].long}
        </li>
      ))}
    </ul>
  );
}

function hPct(x: number): string {
  return `${(x / H.width) * 100}%`;
}

function StationIcon({ station, size }: { station: Station; size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" className="line-map__icon">
      {station.icon.map(d => <path key={d} d={d} />)}
    </svg>
  );
}

function StationText({ station, count, live }: { station: Station; count?: StationCounts[StationId]; live: boolean }) {
  const showSplit = live && count && station.lines.length > 1 && count.S + count.E > 0;
  return (
    <span className="line-map__text">
      <span className="line-map__name">
        {station.name}
        {live && count && <span className="line-map__count">{count.total}<span className="sr-only"> acts</span></span>}
      </span>
      <span className="line-map__sub">{station.sub}</span>
      {showSplit && (
        <span className="line-map__split">
          <span className="line-map__split-item"><span className="line-map__dot line-map__dot--s" aria-hidden="true" />{count.S}<span className="sr-only"> on Line S</span></span>
          <span className="line-map__split-item"><span className="line-map__dot line-map__dot--e" aria-hidden="true" />{count.E}<span className="sr-only"> on Line E</span></span>
        </span>
      )}
    </span>
  );
}

function stateClass(variant: Variant, id: StationId, selected: StationId | null): string {
  if (id === selected) return 'line-map__station--on';
  if (variant === 'explain' && selected && stationIndex(id) < stationIndex(selected)) return 'line-map__station--done';
  return '';
}

export default function LineMap({ variant, selected, onSelect, counts, label, legend = false }: LineMapProps) {
  const live = variant === 'live';
  const rh = RADIUS[variant].h;
  const rv = RADIUS[variant].v;
  const hHeight = H.height[variant];
  const sel = selected ? STATIONS[stationIndex(selected)] : null;

  return (
    <div className={`line-map line-map--${variant}`}>
      {/* Horizontal */}
      <div className="line-map__h" role="group" aria-label={label} style={{ height: hHeight }}>
        <svg className="line-map__track" viewBox={`0 0 ${H.width} ${hHeight}`} preserveAspectRatio="none" aria-hidden="true">
          {H.zones.map(([x0, x1], z) => (
            <rect key={z} className={`line-map__zone line-map__zone--${z}`} x={x0} y={0} width={x1 - x0} height={hHeight} />
          ))}
          <path className="line-map__line line-map__line--e" d={H.lineE} />
          <path className="line-map__line line-map__line--s" d={H.lineS} />
        </svg>
        {legend && <div className="line-map__legend-in"><LineLegend short /></div>}

        {variant === 'explain' && sel && (
          <span
            className="line-map__train"
            aria-hidden="true"
            style={{ left: `calc(${hPct(H.x[sel.id])} - 22px)`, top: (sel.branch ? H.branchY : H.trunkY) - rh - 48 }}
          >
            <span className="line-map__train-body">
              <svg width="22" height="22" viewBox="0 0 24 24">
                <path d="M7 3h10a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3z" />
                <path d="M4 10h16M8 13.5h.01M16 13.5h.01" />
                <path d="M8 17l-2 4M16 17l2 4" />
              </svg>
            </span>
            <span className="line-map__train-pointer" />
          </span>
        )}

        {STATIONS.map(station => {
          const cy = station.branch ? H.branchY : H.trunkY;
          // On the live map the loop station's label sits above it, out of the way of the trunk.
          const labelAbove = live && station.branch;
          const style: React.CSSProperties = {
            left: `calc(${hPct(H.x[station.id])} - ${H.labelWidth / 2}px)`,
            width: H.labelWidth,
            ...(labelAbove ? { top: 4, height: cy + rh - 4 } : { top: cy - rh }),
          };
          return (
            <button
              key={station.id}
              type="button"
              className={`line-map__station ${labelAbove ? 'line-map__station--above' : ''} ${stateClass(variant, station.id, selected)}`}
              style={style}
              aria-pressed={station.id === selected}
              onClick={() => onSelect(station.id)}
            >
              <span className="line-map__medallion" style={{ width: rh * 2, height: rh * 2 }}>
                <StationIcon station={station} size={live ? 24 : 30} />
              </span>
              <StationText station={station} count={counts?.[station.id]} live={live} />
            </button>
          );
        })}

        {H.zones.map(([x0], z) => (
          <span key={z} className={`line-map__zone-label line-map__zone-label--${z}`} style={{ left: `calc(${hPct(x0)} + 14px)`, top: H.zoneLabelY[variant] }} aria-hidden="true">
            Zone {z + 1} · {ZONE_LABELS[z as Zone]}
          </span>
        ))}
      </div>

      {/* Vertical */}
      {legend && <div className="line-map__legend-v"><LineLegend short /></div>}
      <div className="line-map__v" role="group" aria-label={label} style={{ height: V.height }}>
        {V.zones.map(([y0, y1], z) => (
          <span key={z} className={`line-map__vzone line-map__zone--${z}`} style={{ top: y0, height: y1 - y0 }} aria-hidden="true" />
        ))}
        <svg className="line-map__track line-map__track--v" width="120" height={V.height} viewBox={`0 0 120 ${V.height}`} aria-hidden="true">
          <path className="line-map__line line-map__line--e" d={V.lineE} />
          <path className="line-map__line line-map__line--s" d={V.lineS} />
        </svg>
        {STATIONS.map((station, i) => {
          const cy = V.top + V.row * i;
          const cx = station.branch ? V.branchX : V.trunkX;
          return (
            <button
              key={station.id}
              type="button"
              className={`line-map__vstation ${stateClass(variant, station.id, selected)}`}
              style={{ top: cy - V.row / 2, height: V.row, paddingLeft: V.labelX }}
              aria-pressed={station.id === selected}
              onClick={() => onSelect(station.id)}
            >
              <span className="line-map__medallion" style={{ width: rv * 2, height: rv * 2, left: cx - rv, top: V.row / 2 - rv }}>
                <StationIcon station={station} size={22} />
              </span>
              <StationText station={station} count={counts?.[station.id]} live={live} />
            </button>
          );
        })}
      </div>
      <ul className="line-map__vlegend" aria-label="Zones">
        {([0, 1, 2] as Zone[]).map(z => (
          <li key={z}><span className={`line-map__vlegend-swatch line-map__zone--${z}`} aria-hidden="true" />Zone {z + 1}: {ZONE_LABELS[z].toLowerCase()}</li>
        ))}
      </ul>
    </div>
  );
}
